import { ChangeDetectorRef, Component, DestroyRef, inject, Input } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { forkJoin, Observable, Observer } from 'rxjs';
import { map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileDocument } from '../../../models/bewerber.model';
import { FileSelectEvent, FileUpload, FileUploadEvent } from 'primeng/fileupload';
import { TauresFileuploadDragdropPlaceholderComponent, TauresFileuploadFileComponent } from '@taures/taures-components/taures-fileupload';
import { PrimeTemplate } from 'primeng/api';
import { Button } from 'primeng/button';
import { BytesPipe } from '../../../services/bytes.pipe';
import { TauresMessageModule } from '@taures/taures-components/taures-message';
import { Message } from 'primeng/message';
import { Card } from 'primeng/card';

type CvDocumentUpload = {
  data: string;
  contentType: string;
  filename: string;
};

const readFile: (blob: Blob) => Observable<string> = (blob: Blob): Observable<string> =>
  new Observable<string>((observer: Observer<string>): void => {
    const reader: FileReader = new FileReader();

    reader.onerror = (err: ProgressEvent<FileReader>): void => {
      observer.error(err);
    };
    reader.onabort = (err: ProgressEvent<FileReader>): void => {
      observer.error(err);
    };
    reader.onload = (): void => {
      // FileReader.result can be string | ArrayBuffer | null, but readAsDataURL -> string
      observer.next((reader.result as string | null) ?? '');
    };
    reader.onloadend = (): void => {
      observer.complete();
    };

    reader.readAsDataURL(blob);
  });

const base64Size: (data: string) => number = (data: string): number => {
  const y: number = data.endsWith('==') ? 2 : 1;
  return data.length * (3 / 4) - y;
};

const isCvDocumentUploadArray: (value: unknown) => value is CvDocumentUpload[] = (value: unknown): value is CvDocumentUpload[] => {
  if (!Array.isArray(value)) {
    return false;
  }
  return value.every((v: unknown): boolean => {
    if (typeof v !== 'object' || v === null) {
      return false;
    }
    const rec: Record<string, unknown> = v as Record<string, unknown>;
    return typeof rec['data'] === 'string' && typeof rec['contentType'] === 'string' && typeof rec['filename'] === 'string';
  });
};

export function fileSizeValidator(maxBytes: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: unknown = control.value;

    if (!isCvDocumentUploadArray(value)) {
      return null;
    }

    const actualSize: number = value.reduce((prev: number, cur: CvDocumentUpload): number => {
      return prev + base64Size(cur.data);
    }, 0);

    return actualSize > maxBytes ? { fileSize: { max: maxBytes, actual: actualSize } } : null;
  };
}

@Component({
  selector: 'app-dokumente[documents]',
  templateUrl: './dokumente.component.html',
  styleUrls: ['./dokumente.component.scss'],
  imports: [
    TauresFileuploadDragdropPlaceholderComponent,
    PrimeTemplate,
    TauresFileuploadFileComponent,
    Button,
    FileUpload,
    TauresMessageModule,
    Message,
    BytesPipe,
    Card
  ],
  standalone: true
})
export class DokumenteComponent {
  @Input()
  public documents!: FormControl<FileDocument[]>;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  protected onUploadFiles(event: FileUploadEvent | FileSelectEvent): void {
    const pdfFiles: File[] = Array.from(event.files).filter((file: File): boolean => file.type === 'application/pdf');

    const fileLoads: Observable<CvDocumentUpload>[] = pdfFiles.map((file: File): Observable<CvDocumentUpload> => {
      return readFile(file).pipe(
        map((content: string): CvDocumentUpload => ({
          data: content.substring(content.indexOf(',') + 1, content.length),
          contentType: file.type,
          filename: file.name
        }))
      );
    });

    forkJoin(fileLoads)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: CvDocumentUpload[]): void => {
        const currentValue: unknown = this.documents.value;
        const ctrlValue: CvDocumentUpload[] = isCvDocumentUploadArray(currentValue) ? currentValue : [];

        this.documents.setValue([...ctrlValue, ...result]);

        setTimeout((): void => {
          this.changeDetectorRef.markForCheck();
        }, 500);

        this.changeDetectorRef.markForCheck();
      });
  }

  protected size(data: string): number {
    return base64Size(data);
  }

  protected choose(_: unknown, callback: VoidFunction): void {
    callback();
  }

  protected remove(i: number): void {
    const currentValue: unknown = this.documents.value;
    const ctrlValue: CvDocumentUpload[] = isCvDocumentUploadArray(currentValue) ? [...currentValue] : [];

    ctrlValue.splice(i, 1);
    this.documents.setValue(ctrlValue);
  }
}
